import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator/loading-indicator';
import { getCurrentOrgId, isModernFeatureManagementEnabled, patchConfigInfo, } from '@thoughtspot/session-service';
import { patchSystemConfig } from '@thoughtspot/system-config-service';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BasePageContext } from "../../../contexts/basepage-context";
import { Admin__FeatureManagementScope, useSetFeatureManagementValueMutation, } from '../../../services/feature-management/feature-management-service';
import { getViewMode } from '../../settings/settings.container.util';
import { AdminPageHeader, } from '../admin-page-header/admin-page-header';
import { ApplicationSettingsGroup, ApplicationSettingsTranslationKeys as Keys, } from '../constants';
import { FeatureAccessTab } from '../feature-management/feature-management.util';
import { OverrideOption, PreserveOverrideModal, } from '../feature-management/preserve-override-modal/preserve-override-modal';
import styles from './application-settings.module.scss';
import { ApplicationSettingsTab } from './application-settings-tab';
import { useTscliGroup } from './hooks/use-tscli-group';
import { ResetConfirmModal } from './reset-confirm-modal';
const GROUP_GENERAL = ApplicationSettingsGroup.GENERAL;
const GROUP_DATA_MODELLING = ApplicationSettingsGroup.DATA_MODELLING;
const GROUP_CSV_UPLOAD = ApplicationSettingsGroup.CSV_UPLOAD;
const INTERCOM_ENABLED_KEY = 'orion.intercomEnabled';
const hasConfigs = (groupConfig) => {
    if (!groupConfig)
        return false;
    return (groupConfig.settings ?? []).some(s => s && (s.configs ?? []).some(Boolean));
};
export const ApplicationSettingsContainer = ({ currentOrgId, }) => {
    const { t } = useTranslation();
    const { metricsService } = BasePageContext();
    const resolvedOrgId = currentOrgId ?? getCurrentOrgId();
    const viewMode = getViewMode(resolvedOrgId);
    const [activeTabId, setActiveTabId] = useState(GROUP_GENERAL);
    const [toast, setToast] = useState({
        isVisible: false,
        message: '',
        isError: false,
    });
    const [resetModal, setResetModal] = useState({
        isOpen: false,
        groupName: '',
        configKeys: [],
    });
    const [preserveModal, setPreserveModal] = useState({
        isOpen: false,
        configName: '',
        configLabel: '',
        configValue: '',
        previousValue: '',
        groupName: '',
        onDismiss: _.noop,
    });
    const generalSettings = useTscliGroup(GROUP_GENERAL);
    const dataModelling = useTscliGroup(GROUP_DATA_MODELLING);
    const csvUpload = useTscliGroup(GROUP_CSV_UPLOAD);
    const fetchedForViewMode = useRef(null);
    useEffect(() => {
        if (fetchedForViewMode.current === viewMode)
            return;
        fetchedForViewMode.current = viewMode;
        generalSettings.fetch(viewMode);
        dataModelling.fetch(viewMode);
        csvUpload.fetch(viewMode);
    }, [viewMode, generalSettings.fetch, dataModelling.fetch, csvUpload.fetch]);
    const { setFeatureManagementValue, } = useSetFeatureManagementValueMutation();
    const allFetching = generalSettings.isFetching ||
        dataModelling.isFetching ||
        csvUpload.isFetching;
    const tabs = [
        {
            id: GROUP_GENERAL,
            title: t(Keys.TAB_GENERAL),
            show: !allFetching && hasConfigs(generalSettings.groupConfig),
        },
        {
            id: GROUP_DATA_MODELLING,
            title: t(Keys.TAB_DATA_MODELLING),
            show: !allFetching && hasConfigs(dataModelling.groupConfig),
        },
        {
            id: GROUP_CSV_UPLOAD,
            title: t(Keys.TAB_CSV_UPLOAD),
            show: !allFetching && hasConfigs(csvUpload.groupConfig),
        },
    ];
    useEffect(() => {
        if (allFetching)
            return;
        const firstVisible = tabs.find(tab => tab.show);
        if (firstVisible && !tabs.find(t => t.id === activeTabId && t.show)) {
            setActiveTabId(firstVisible.id);
        }
    }, [allFetching, activeTabId]);
    const showSuccess = useCallback((message) => {
        setToast({ isVisible: true, message, isError: false });
    }, []);
    const showError = useCallback((message, retryFn) => {
        setToast({ isVisible: true, message, isError: true, retryFn });
    }, []);
    const retryFn = useCallback(() => {
        if (generalSettings.lastError)
            generalSettings.retry();
        else if (dataModelling.lastError)
            dataModelling.retry();
        else if (csvUpload.lastError)
            csvUpload.retry();
    }, [generalSettings, dataModelling, csvUpload]);
    const handleSave = useCallback((groupName, rawOptions, vMode) => {
        const groupHook = {
            [GROUP_GENERAL]: generalSettings,
            [GROUP_DATA_MODELLING]: dataModelling,
            [GROUP_CSV_UPLOAD]: csvUpload,
        }[groupName];
        if (!groupHook)
            return;
        groupHook.save(rawOptions, vMode ?? viewMode, {
            onMutationSuccess: () => {
                rawOptions.forEach(opt => {
                    patchConfigInfo(opt.optionKey, opt.optionValue);
                    if (opt.optionKey === INTERCOM_ENABLED_KEY) {
                        patchSystemConfig('intercomEnabled', opt.optionValue);
                    }
                });
            },
            onSuccess: () => {
                if (groupName === GROUP_CSV_UPLOAD) {
                    metricsService?.collectEvent(new UserAction(userActionTypeEnum.CSV_UPLOAD_EMBRACE_CONFIGURED_FROM_ADMIN_PORTAL), {});
                }
                showSuccess(t(Keys.SAVE_SUCCESS));
            },
            onError: () => showError(t(Keys.SAVE_ERROR), retryFn),
        });
    }, [
        generalSettings,
        dataModelling,
        csvUpload,
        viewMode,
        showSuccess,
        showError,
        retryFn,
        t,
        metricsService,
    ]);
    const handleReset = useCallback((groupName, configKeys) => {
        setResetModal({ isOpen: true, groupName, configKeys });
    }, []);
    const confirmReset = useCallback(() => {
        const { groupName, configKeys } = resetModal;
        const groupHook = {
            [GROUP_GENERAL]: generalSettings,
            [GROUP_DATA_MODELLING]: dataModelling,
            [GROUP_CSV_UPLOAD]: csvUpload,
        }[groupName];
        setResetModal({ isOpen: false, groupName: '', configKeys: [] });
        if (!groupHook)
            return;
        groupHook.reset(configKeys, viewMode, {
            onSuccess: refreshed => {
                if (refreshed) {
                    const keySet = new Set(configKeys);
                    (refreshed.settings ?? []).forEach(setting => (setting?.configs ?? []).forEach(config => {
                        if (config?.configName &&
                            keySet.has(config.configName) &&
                            config.value !== undefined) {
                            patchConfigInfo(config.configName, config.value);
                            if (config.configName === INTERCOM_ENABLED_KEY)
                                patchSystemConfig('intercomEnabled', config.value);
                        }
                    }));
                }
                showSuccess(t(Keys.RESET_SUCCESS));
            },
            onError: () => showError(t(Keys.RESET_ERROR), retryFn),
        });
    }, [
        resetModal,
        generalSettings,
        dataModelling,
        csvUpload,
        viewMode,
        showSuccess,
        showError,
        retryFn,
        t,
    ]);
    const refetchGroup = useCallback((groupName) => {
        const groupHook = {
            [GROUP_GENERAL]: generalSettings,
            [GROUP_DATA_MODELLING]: dataModelling,
            [GROUP_CSV_UPLOAD]: csvUpload,
        }[groupName];
        groupHook?.fetch(viewMode);
    }, [generalSettings, dataModelling, csvUpload, viewMode]);
    const handlePreserveOverrideNeeded = useCallback((configName, configValue, configLabel, previousValue, onDismiss) => {
        setPreserveModal({
            isOpen: true,
            groupName: activeTabId,
            configName,
            configLabel,
            configValue,
            previousValue,
            onDismiss,
        });
    }, [activeTabId]);
    const handlePreserveUpdate = useCallback((option) => {
        setPreserveModal(prev => ({ ...prev, isOpen: false }));
        setFeatureManagementValue({
            feature_identifier: preserveModal.configName,
            feature_value: preserveModal.configValue,
            scope: Admin__FeatureManagementScope.Cluster,
            reset_org_overrides: option === OverrideOption.IGNORE,
        }, () => {
            patchConfigInfo(preserveModal.configName, preserveModal.configValue);
            if (preserveModal.configName === INTERCOM_ENABLED_KEY) {
                patchSystemConfig('intercomEnabled', preserveModal.configValue);
            }
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_FEATURE_OVERRIDE), {
                featureId: preserveModal.configName,
                featureName: preserveModal.configLabel,
                previousValue: preserveModal.previousValue,
                newValue: preserveModal.configValue,
                featureCategory: FeatureAccessTab.GeneralAccess,
            });
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.FEATURE_OVERRIDE_PRESERVED), {
                featureId: preserveModal.configName,
                featureName: preserveModal.configLabel,
                selectedOption: option,
            });
            showSuccess(t(Keys.SAVE_SUCCESS));
            refetchGroup(preserveModal.groupName);
        }, () => showError(t(Keys.SAVE_ERROR), () => handlePreserveUpdate(option)));
    }, [
        preserveModal,
        metricsService,
        setFeatureManagementValue,
        showSuccess,
        showError,
        refetchGroup,
        t,
    ]);
    const handleSaveOrgAware = useCallback((configName, configValue, previousValue, onError) => {
        const orgId = getCurrentOrgId();
        setFeatureManagementValue({
            feature_identifier: configName,
            feature_value: configValue,
            scope: Admin__FeatureManagementScope.Org,
            org_identifier: orgId,
        }, () => {
            patchConfigInfo(configName, configValue);
            if (configName === INTERCOM_ENABLED_KEY) {
                patchSystemConfig('intercomEnabled', configValue);
            }
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.ORG_FEATURE_OVERRIDE), {
                configName,
                previousValue,
                newValue: configValue,
                orgId,
                featureCategory: FeatureAccessTab.GeneralAccess,
                source: 'app-settings',
            });
            showSuccess(t(Keys.SAVE_SUCCESS));
        }, () => {
            onError();
            showError(t(Keys.SAVE_ERROR), () => handleSaveOrgAware(configName, configValue, previousValue, onError));
        });
    }, [metricsService, setFeatureManagementValue, showSuccess, showError, t]);
    const isSaving = generalSettings.isSaving ||
        dataModelling.isSaving ||
        csvUpload.isSaving;
    const isModernFMEnabled = isModernFeatureManagementEnabled();
    const tabProps = {
        isSaving,
        viewMode,
        isModernFMEnabled,
        onSave: handleSave,
        onSaveOrgAware: handleSaveOrgAware,
        onReset: handleReset,
        onPreserveOverrideNeeded: handlePreserveOverrideNeeded,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(LoadingIndicator.Global, { hideLoader: !allFetching, className: styles.loader }, !allFetching && (React.createElement(React.Fragment, null,
            React.createElement(AdminPageHeader, { title: t(Keys.TITLE), tabs: tabs, selectedTabId: activeTabId, onTabChange: setActiveTabId }),
            React.createElement("div", { className: styles.tabContent },
                activeTabId === GROUP_GENERAL && (React.createElement(ApplicationSettingsTab, { groups: generalSettings.groupConfig
                        ? [generalSettings.groupConfig]
                        : [], isLoading: generalSettings.isBackgroundFetching, ...tabProps })),
                activeTabId === GROUP_DATA_MODELLING && (React.createElement(ApplicationSettingsTab, { groups: dataModelling.groupConfig
                        ? [dataModelling.groupConfig]
                        : [], isLoading: dataModelling.isBackgroundFetching, ...tabProps })),
                activeTabId === GROUP_CSV_UPLOAD && (React.createElement(ApplicationSettingsTab, { groups: csvUpload.groupConfig
                        ? [csvUpload.groupConfig]
                        : [], isLoading: csvUpload.isBackgroundFetching, ...tabProps })))))),
        React.createElement(ResetConfirmModal, { isOpen: resetModal.isOpen, onConfirm: confirmReset, onDismiss: () => setResetModal({
                isOpen: false,
                groupName: '',
                configKeys: [],
            }) }),
        React.createElement(PreserveOverrideModal, { isOpen: preserveModal.isOpen, configName: preserveModal.configName, configLabel: preserveModal.configLabel, onDismiss: () => {
                preserveModal.onDismiss();
                setPreserveModal(prev => ({ ...prev, isOpen: false }));
            }, onUpdate: handlePreserveUpdate }),
        React.createElement(GlobalAlert.Toast, { message: toast.message, isVisible: toast.isVisible, onClose: () => setToast(prev => ({ ...prev, isVisible: false })), autoHide: !toast.isError, showCheckmarkIcon: !toast.isError, primaryAction: toast.isError && toast.retryFn
                ? {
                    label: t(Keys.RETRY),
                    onClick: toast.retryFn,
                }
                : undefined })));
};
//# sourceMappingURL=application-settings.container.js.map